import { keepPreviousData, queryOptions } from '@tanstack/react-query';
import { apiClient } from '@/api/axios';
import { API } from '@/api/endpoints';
import { STALE_TIME } from '@/api/staleTimes';
import type { PcmpQueueFilters } from '../types/pcmpQueue.types';
import {
  pcmpRequestActedSchema,
  pcmpRequestActResultSchema,
  pcmpRequestedCountsSchema,
  pcmpRequestedListSchema,
  type PcmpRequestActed,
  type PcmpRequestActPayload,
  type PcmpRequestActResult,
  type PcmpRequestDecision,
  type PcmpRequestedCounts,
  type PcmpRequestedList,
  type PcmpRequestedParams,
} from '../types/pcmpRequested.types';
import { pcmpRequestQueryKeys } from './pcmpRequests.api';

export const pcmpRequestedQueryOptions = (params: PcmpRequestedParams) =>
  queryOptions({
    queryKey: pcmpRequestQueryKeys.requested(params),
    queryFn: async (): Promise<PcmpRequestedList> => {
      const res = await apiClient.get(API.pcmpRequest.requested, { params });
      return pcmpRequestedListSchema.parse(res.data);
    },
    placeholderData: keepPreviousData,
    staleTime: STALE_TIME,
  });

/** The tabs and their badges; one tab per active category, so it follows the categories. */
export const pcmpRequestedCountsQueryOptions = (filters: PcmpQueueFilters) =>
  queryOptions({
    queryKey: pcmpRequestQueryKeys.requestedCounts(filters),
    queryFn: async (): Promise<PcmpRequestedCounts> => {
      const res = await apiClient.get(API.pcmpRequest.requestedCounts, { params: filters });
      return pcmpRequestedCountsSchema.parse(res.data);
    },
    placeholderData: keepPreviousData,
    staleTime: STALE_TIME,
  });

export async function actOnPcmpRequests(
  payload: PcmpRequestActPayload,
): Promise<PcmpRequestActResult> {
  const res = await apiClient.post(API.pcmpRequest.act, payload);
  return pcmpRequestActResultSchema.parse(res.data);
}

const SINGLE_ENDPOINT = {
  PROCESS: API.pcmpProposal.requestProcess,
  APPROVE: API.pcmpProposal.requestApprove,
  REJECT: API.pcmpProposal.requestReject,
} as const satisfies Record<PcmpRequestDecision, (id: number) => string>;

/** The details page's form: act on the one open request of a proposal. */
export async function actOnPcmpProposalRequest(
  proposalId: number,
  decision: PcmpRequestDecision,
  comment: string | undefined,
): Promise<PcmpRequestActed> {
  const res = await apiClient.post(SINGLE_ENDPOINT[decision](proposalId), { comment });
  return pcmpRequestActedSchema.parse(res.data);
}
